'use client';

import Amount from '@/components/Calculators/EarnCalculator/Amount';
import Coin from '@/components/Calculators/EarnCalculator/Coin';
import LockPeriods from '@/components/Calculators/EarnCalculator/LockPeriods';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import useUnstable_LockPeriod from '@/hooks/useUnstable_LockPeriod';
import useVault from '@/hooks/useVault';
import { useState } from 'react';

export default function Calculator({ labels, vaults = [], precisions = {}, notice, timeUnitLabels = {} }) {
  const {
    earn_calculator,
    select_your_coin,
    enter_investment_amount_in_dollar,
    choose_your_lock_period,
    your_apy,
    daily_earnings,
    monthly_earnings,
    yearly_earnings,
    earn_now,
  } = labels;

  const defaultValue = vaults[0];

  const [amount, setAmount] = useState(0);
  const [currency, setCurrency] = useState(defaultValue?.currency_id);
  const { lockPeriod, lockPeriods, setLockPeriod } = useUnstable_LockPeriod(vaults, currency, timeUnitLabels);
  const vault = useVault(vaults, currency, lockPeriod);

  const dailyEarnings = (vault?.annual_rate_percent / 100) * (amount / 365);
  const monthlyEarnings = dailyEarnings * 30;
  const yearlyEarnings = (vault?.annual_rate_percent / 100) * amount;

  if (!lockPeriods.length || !vaults.length) return <></>;

  return (
    <div
      className='earn-caclulator flex flex-col gap-6 border border-primary/10 rounded-xl p-6 sm:p-8 max-w-screen-md w-full'
      style={{
        backgroundImage: 'linear-gradient(180deg, hsla(var(--primary)/0.05) 0%, hsla(var(--primary)/0.20) 100%)'
      }}
    >
      <p className='text-3xl font-bold text-primary mb-4'>{earn_calculator}</p>
      <div className='flex flex-col gap-4'>
        {<p className='h5'>{select_your_coin}</p>}
        <Coin placeholder={select_your_coin} vaults={vaults} onChange={setCurrency} />
      </div>

      <div className='flex flex-col gap-4'>
        {<p className='h5'>{enter_investment_amount_in_dollar}</p>}
        <Amount value={amount} onChange={setAmount} />
      </div>

      <div className='flex flex-col gap-4'>
        {<p className='h5'>{choose_your_lock_period}</p>}
        <LockPeriods lockPeriods={lockPeriods} onChange={setLockPeriod} />
      </div>

      <ScrollArea>
        <div
          className='grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-4 rounded-2xl px-4 sm:px-6 pt-6 pb-6 sm:pb-14 border border-secondary/10 backdrop-blur text-center md:text-start mt-4'
          style={{
            backgroundImage:
              'linear-gradient(176deg, hsla(var(--secondary)/0.05) -29.28%, hsla(var(--secondary)/0.25) 47.4%)'
          }}
        >
          <div className='flex flex-col gap-1'>
            <span className='font-semibold'>{your_apy}</span>
            <span className='text-primary h3 drop-shadow-xl'>{vault ? `${vault.annual_rate_percent}%` : 'N/A'}</span>
          </div>
          <div className='flex flex-col gap-1'>
            <span className='font-semibold'>{daily_earnings}</span>
            <span className='text-primary h3 drop-shadow-xl'>
              {Number.isNaN(dailyEarnings) ? 0 : `$${dailyEarnings.toFixed(precisions[currency])}`}
            </span>
          </div>
          <div className='flex flex-col gap-1'>
            <span className='font-semibold'>{monthly_earnings}</span>
            <span className='text-primary h3 drop-shadow-xl'>
              {Number.isNaN(monthlyEarnings) ? 0 : `$${monthlyEarnings.toFixed(precisions[currency])}`}
            </span>
          </div>
          <div className='flex flex-col gap-1'>
            <span className='font-semibold'>{yearly_earnings}</span>
            <span className='text-primary h3 drop-shadow-xl'>
              {Number.isNaN(yearlyEarnings) ? 0 : `$${yearlyEarnings.toFixed(precisions[currency])}`}
            </span>
          </div>
        </div>
        <ScrollBar orientation='horizontal' classNames={{ thumb: 'bg-primary/25 rounded-none' }} />{' '}
      </ScrollArea>

      <Button
        external
        href='/refer-and-earn'
        className='sm:-translate-y-1/2 sm:-mt-6 sm:max-w-60 w-full mx-auto'
        size='lg'
      >
        {earn_now}
      </Button>

      {notice && <div className='text-center text-muted text-[10px] text-zinc-400'>{notice}</div>}
    </div>
  );
}
